import { makeStyles, Theme, Grid, Paper } from '@material-ui/core';

import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';
import {
  catalogApiRef,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';
import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';

import { SearchType } from '@backstage/plugin-search';
import {
  SearchBar,
  SearchFilter,
  SearchResult,
  SearchResultListItemExtensions,
  SearchPagination,
  useSearch,
} from '@backstage/plugin-search-react';
import {
  CatalogIcon,
  Content,
  DocsIcon,
  Header,
  Page,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { cleanIndexText } from './docsExcerpt';

/**
 * A documentation result with its text cleaned the way the palette cleans it
 * (DX-341): without the `¶` beside every heading and the line numbers of code
 * blocks, which the index reads as words. Other results pass through as they
 * came. The matched-word tags stay in the text; the result row marks them.
 */
const withCleanText = <
  R extends {
    type: string;
    document: { text: string };
    highlight?: { fields: Record<string, string> };
  },
>(
  result: R,
): R => {
  if (result.type !== 'techdocs') {
    return result;
  }
  const marked = result.highlight?.fields.text;
  return {
    ...result,
    document: {
      ...result.document,
      text: cleanIndexText(result.document.text),
    },
    highlight:
      result.highlight && marked !== undefined
        ? {
            ...result.highlight,
            fields: {
              ...result.highlight.fields,
              text: cleanIndexText(marked),
            },
          }
        : result.highlight,
  };
};

const useStyles = makeStyles((theme: Theme) => ({
  bar: {
    padding: theme.spacing(1, 0),
  },
  filters: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  filter: {
    '& + &': {
      marginTop: theme.spacing(2.5),
    },
  },
}));

const SearchPage = () => {
  const classes = useStyles();
  const { types } = useSearch();
  const catalogApi = useApi(catalogApiRef);

  return (
    <Page themeId="home">
      <Header title="Search" />
      <Content>
        <Grid container direction="row">
          <Grid item xs={12}>
            <Paper className={classes.bar}>
              <SearchBar />
            </Paper>
          </Grid>
          <Grid item xs={3}>
            {/* No default type. `defaultValue` is applied after the page has
                read its address, so it overrode a type the address named: the
                palette's "All results in the documentation" (DX-341) landed on
                the software catalog instead. Without it the address decides,
                and an address naming no type shows every result. */}
            <SearchType.Accordion
              name="Result Type"
              types={[
                {
                  value: 'software-catalog',
                  name: 'Software Catalog',
                  icon: <CatalogIcon />,
                },
                {
                  value: 'techdocs',
                  name: 'Documentation',
                  icon: <DocsIcon />,
                },
              ]}
            />
            <Paper className={classes.filters}>
              {types.includes('techdocs') && (
                <SearchFilter.Select
                  className={classes.filter}
                  label="Entity"
                  name="name"
                  values={async () => {
                    // Return a list of entities which are documented.
                    const { items } = await catalogApi.getEntities({
                      fields: ['metadata.name'],
                      filter: {
                        'metadata.annotations.backstage.io/techdocs-ref':
                          CATALOG_FILTER_EXISTS,
                      },
                    });

                    const names = items.map(entity => entity.metadata.name);
                    names.sort();
                    return names;
                  }}
                />
              )}
              <SearchFilter.Select
                className={classes.filter}
                label="Kind"
                name="kind"
                values={['Component', 'Template']}
              />
              <SearchFilter.Checkbox
                className={classes.filter}
                label="Lifecycle"
                name="lifecycle"
                values={['experimental', 'production']}
              />
            </Paper>
          </Grid>
          <Grid item xs={9}>
            <SearchPagination />
            {/* The results as SearchResult would list them itself, handed the
                cleaned text. Its loading, error and empty states are drawn
                before this is called, so they are Backstage's unchanged. */}
            <SearchResult>
              {({ results }) => (
                <SearchResultListItemExtensions
                  results={results.map(withCleanText)}
                >
                  <CatalogSearchResultListItem icon={<CatalogIcon />} />
                  <TechDocsSearchResultListItem icon={<DocsIcon />} />
                </SearchResultListItemExtensions>
              )}
            </SearchResult>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};

export const searchPage = <SearchPage />;
